/**
 * Owner-studio theme controller + context.
 *
 * Owns the live appearance state (mode + accent), resolves `auto` against the OS
 * `prefers-color-scheme`, writes the `<html>` attributes the generated stylesheet
 * keys off, keeps the glow helpers' active mode in sync, and persists the choice
 * to localStorage (instant, for the no-flash boot script) and the preferences API
 * (cross-device).
 *
 * The variable stylesheet itself is injected ONCE as a plain `<style>` (built from
 * the registry) — at module load, so it's in the DOM before the studio first
 * paints; the boot script in index.html has already applied the saved attributes,
 * so there's no theme flash.
 */

import React from 'react';

import { useAppDispatch, useAppSelector } from '@/store';
import { fetchUserPreferences, saveUserPreferences, selectLanguagePref } from '@/store/slices/userPreferences';
import { changeLanguage } from '@/store/slices/application';
import { readManualLanguagePick } from '@/nova/config/manualLanguagePick';
import { toAppLang } from '@/nova/config/langCodeMap';
import i18n from '@/utils/i18n';

import { buildThemeCss } from './buildThemeCss';
import { setActiveThemeMode } from './themeGlows';
import { usePersistStartTracking } from './usePersistStartTracking';
import {
  ACCENT_ATTR,
  APPEARANCE_PREF_KEY,
  DEFAULT_ACCENT,
  DEFAULT_THEME_MODE,
  THEME_ATTR,
  THEME_MODE_STORAGE_KEY,
  isThemeMode,
  type Accent,
  type AppearancePrefs,
  type ResolvedMode,
  type ThemeMode,
} from './themeConfig';

/* ── one-time stylesheet injection ───────────────────────────────────────── */

const STYLE_EL_ID = 'ows-theme-vars';

const ensureThemeStyleInjected = (): void => {
  if (typeof document === 'undefined') return;
  let el = document.getElementById(STYLE_EL_ID) as HTMLStyleElement | null;
  const css = buildThemeCss();
  if (!el) {
    el = document.createElement('style');
    el.id = STYLE_EL_ID;
    el.textContent = css;
    // Prepend to <head> so app styles / styled-components (later in the cascade)
    // can still override individual properties if they ever need to.
    document.head.insertBefore(el, document.head.firstChild);
  } else if (el.textContent !== css) {
    el.textContent = css; // HMR refresh
  }
};

// Inject as soon as this module evaluates (before React renders studio content).
ensureThemeStyleInjected();

/* ── system preference ───────────────────────────────────────────────────── */

const prefersDark = (): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-color-scheme: dark)').matches;

const resolveMode = (mode: ThemeMode, systemDark: boolean): ResolvedMode =>
  mode === 'auto' ? (systemDark ? 'dark' : 'light') : mode;

const readStored = (key: string): string | null => {
  try {
    return typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  } catch {
    return null;
  }
};
const writeStored = (key: string, value: string): void => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode / quota — the prefs API still carries it cross-session */
  }
};

/* ── context ─────────────────────────────────────────────────────────────── */

export type ThemeControls = {
  /** The user's choice. */
  mode: ThemeMode;
  accent: Accent;
  /** What `mode` currently resolves to (auto → light|dark). */
  resolved: ResolvedMode;
  /** The OS preference right now, independent of `mode` — what `auto` WOULD pick.
   *  (The Auto option's hint shows this, not `resolved`.) */
  system: ResolvedMode;
  setMode: (mode: ThemeMode) => void;
};

const ThemeContext = React.createContext<ThemeControls | null>(null);

/** Full theme controls (for the Settings → Interface section). */
export const useThemeControls = (): ThemeControls => {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) throw new Error('useThemeControls must be used within OwsThemeProvider');
  return ctx;
};

/** Resolved appearance (`light` | `dark`) — for components whose styling can't be
 *  a pure CSS variable (the score-driven glows). Subscribing re-renders them on a
 *  theme switch so they recompute. Falls back to `dark` outside the provider. */
export const useThemeMode = (): ResolvedMode => React.useContext(ThemeContext)?.resolved ?? 'dark';

/* ── provider ────────────────────────────────────────────────────────────── */

export const OwsThemeProvider = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const dispatch = useAppDispatch();

  // App-wide, once-per-account persistence of the marketing-site ad-attribution hand-off
  // (`startProperties.tracking`). Lives here (the only provider wrapping every route) so it lands
  // even for owners who never reach the configurator. See `./usePersistStartTracking.ts`.
  usePersistStartTracking();

  const [mode, setModeState] = React.useState<ThemeMode>(() => {
    const stored = readStored(THEME_MODE_STORAGE_KEY);
    return isThemeMode(stored) ? stored : DEFAULT_THEME_MODE;
  });
  // The app has a single brand colour (orange #ff791b) — no accent variants.
  const accent: Accent = DEFAULT_ACCENT;
  const [systemDark, setSystemDark] = React.useState<boolean>(prefersDark);

  // Track OS preference while in (or able to enter) auto mode.
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e: MediaQueryListEvent): void => setSystemDark(e.matches);
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, []);

  // Adopt the cross-device saved appearance the first time preferences load —
  // unless the user has already changed it this session (then local wins).
  const userTouched = React.useRef(false);
  const savedAppearance = useAppSelector(
    (state) => state.userPreferences.preferences[APPEARANCE_PREF_KEY] as Partial<AppearancePrefs> | undefined,
  );
  const prefsLoaded = useAppSelector((state) => state.userPreferences.loaded);
  const adopted = React.useRef(false);
  React.useEffect(() => {
    if (!prefsLoaded || adopted.current || userTouched.current) return;
    adopted.current = true;
    if (!savedAppearance) return;
    if (isThemeMode(savedAppearance.mode)) {
      setModeState(savedAppearance.mode);
      writeStored(THEME_MODE_STORAGE_KEY, savedAppearance.mode);
    }
  }, [prefsLoaded, savedAppearance]);

  // ── saved UI-language adoption (app-wide) ──────────────────────────────────
  // Preferences power the cross-device language too, but unlike appearance the
  // configurator wizard runs OUTSIDE the studio layout, so it can't rely on
  // `MainLayout` to load them. Fetch them here (the only provider that wraps every
  // route) once authenticated, then adopt the saved language the first time they
  // land. Compared against the LIVE i18next language (not just the store) so a stale
  // boot language is corrected without the user having to toggle the switcher.
  // Gate on a CONFIRMED identity (a resolved `user_id`), not bare token presence:
  // the prefs fetch tags the result with the current `user_id` so a later identity
  // change (impersonation without logout) can invalidate it. Firing on the boot
  // token before `/auth/check` resolves the user would tag the prefs with a null
  // identity and force a needless reset (theme/language flash) once it lands.
  const authenticated = useAppSelector((state) => state.authentication.user.user_id != null);
  const prefsLoading = useAppSelector((state) => state.userPreferences.loading);
  const savedLanguage = useAppSelector(selectLanguagePref);
  React.useEffect(() => {
    if (authenticated && !prefsLoaded && !prefsLoading) dispatch(fetchUserPreferences());
  }, [authenticated, prefsLoaded, prefsLoading, dispatch]);

  const languageAdopted = React.useRef(false);
  React.useEffect(() => {
    if (!authenticated || !prefsLoaded || languageAdopted.current) return;
    languageAdopted.current = true;
    // A `startProperties` locale hand-off from the external marketing site (set at boot in
    // `src/dev.js`, consumed in `pages/configurator-v3/lib/startProperties.ts`) is an EXPLICIT
    // one-time override for this session — it wins over the saved cross-device pref. Skip
    // adoption so we don't revert it (the configurator persists the hand-off locale to prefs
    // itself, so future sessions inherit it the normal way). Key mirrors START_LOCALE_OVERRIDE_KEY.
    let startLocaleOverride: string | null = null;
    try {
      startLocaleOverride = sessionStorage.getItem('ows-start-locale-override');
    } catch {
      startLocaleOverride = null;
    }
    if (startLocaleOverride && (window.config.languages ?? []).includes(startLocaleOverride)) return;
    // An EXPLICIT in-app language pick this session (login / register / configurator / Settings)
    // is the user's source of truth, so never revert it to the fetched pref. This is the plain
    // language sibling of the startProperties override above (that one also cues the deploy
    // region; this one is language-only, see nova/config/manualLanguagePick). The configurator
    // flush persists the pick to prefs, so future sessions inherit it the normal way.
    const manualPick = readManualLanguagePick();
    if (manualPick && (window.config.languages ?? []).includes(manualPick)) return;
    // Preferences store the BACKEND language code; map it to the app i18n code (e.g. `ar` -> `arg`).
    const appSaved = savedLanguage ? toAppLang(savedLanguage) : savedLanguage;
    if (!appSaved || !(window.config.languages ?? []).includes(appSaved)) return;
    // `changeLanguage` re-translates (i18next) + syncs the store + `language` localStorage.
    if (appSaved !== (i18n.language || '').split('-')[0]) dispatch(changeLanguage(appSaved));
  }, [authenticated, prefsLoaded, savedLanguage, dispatch]);

  const resolved = resolveMode(mode, systemDark);

  // Apply to <html> + keep the glow helpers' active mode in sync.
  React.useEffect(() => {
    ensureThemeStyleInjected();
    if (typeof document !== 'undefined') {
      const el = document.documentElement;
      el.setAttribute(THEME_ATTR, resolved);
      el.setAttribute(ACCENT_ATTR, accent);
      el.style.colorScheme = resolved; // native form controls / scrollbars follow
    }
    setActiveThemeMode(resolved);
  }, [resolved, accent]);

  // Persist the appearance choice to localStorage (instant) + the preferences API.
  const setMode = React.useCallback(
    (next: ThemeMode) => {
      userTouched.current = true;
      setModeState(next);
      writeStored(THEME_MODE_STORAGE_KEY, next);
      const payload: AppearancePrefs = { mode: next, accent: DEFAULT_ACCENT };
      void dispatch(saveUserPreferences({ [APPEARANCE_PREF_KEY]: payload }));
    },
    [dispatch],
  );

  const system: ResolvedMode = systemDark ? 'dark' : 'light';
  const value = React.useMemo<ThemeControls>(
    () => ({ mode, accent, resolved, system, setMode }),
    [mode, accent, resolved, system, setMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
